import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface PlatformCompanySummary {
  company_id: string;
  company_name: string;
  project_count: number;
  plan_tier: string;
  subscription_status: string;
  monthly_rate: number;
  seat_count: number;
  included_seats: number;
  price_per_additional_seat: number;
  included_contractors: number;
  contractor_block_size: number;
  contractor_block_price: number;
  trial_ends_at: string | null;
  active_users: number;
  paid_seat_count: number;
  contractor_count: number;
  calculated_mrr: number;
}

export interface CompanyUser {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  role_name: string | null;
  status: string | null;
  last_login: string | null;
}

export interface CompanyProject {
  id: string;
  name: string;
  project_number: string | null;
  status: string;
  created_at: string;
}

export const usePlatformSummary = () => {
  return useQuery({
    queryKey: ["platform-summary"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_platform_summary");
      if (error) throw error;
      return (data ?? []) as unknown as PlatformCompanySummary[];
    },
    staleTime: 60_000,
  });
};

export const useIsPlatformAdmin = () => {
  return useQuery({
    queryKey: ["is-platform-admin"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("is_platform_admin");
      if (error) return false;
      return data === true;
    },
    staleTime: 5 * 60_000,
  });
};

export const useCompanyUsersForPlatform = (companyId: string | null) => {
  return useQuery({
    queryKey: ["platform-company-users", companyId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_company_users_for_platform", {
        p_company_id: companyId!,
      });
      if (error) throw error;
      return (data ?? []) as CompanyUser[];
    },
    enabled: !!companyId,
    staleTime: 60_000,
  });
};

export const useCompanyProjectsForPlatform = (companyId: string | null) => {
  return useQuery({
    queryKey: ["platform-company-projects", companyId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_company_projects_for_platform", {
        p_company_id: companyId!,
      });
      if (error) throw error;
      return (data ?? []) as CompanyProject[];
    },
    enabled: !!companyId,
    staleTime: 60_000,
  });
};

export const useUpdateSubscription = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      companyId: string;
      planTier: string;
      status: string;
      monthlyRate: number;
      seatCount: number;
      includedSeats: number;
      pricePerAdditionalSeat: number;
      includedContractors: number;
      contractorBlockSize: number;
      contractorBlockPrice: number;
      pricingNote: string | null;
    }) => {
      const { error } = await supabase.rpc("update_company_subscription" as any, {
        p_company_id: params.companyId,
        p_plan_tier: params.planTier,
        p_status: params.status,
        p_monthly_rate: params.monthlyRate,
        p_seat_count: params.seatCount,
        p_included_seats: params.includedSeats,
        p_price_per_additional_seat: params.pricePerAdditionalSeat,
        p_included_contractors: params.includedContractors,
        p_contractor_block_size: params.contractorBlockSize,
        p_contractor_block_price: params.contractorBlockPrice,
        p_pricing_note: params.pricingNote,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["platform-summary"] });
    },
  });
};
